import clsx from "clsx";
import { FunctionComponent } from "react";

import type {
  ContentCardBaseConfig,
  ContentCardArticle,
} from "@ui/components/content-cards/content-cards.types";
import ContentBlockMeta from "@ui/components/content-cards/ContentBlock/components/ContentBlockMeta";
import ContentBlock from "@ui/components/content-cards/ContentBlock/ContentBlock";
import ContentCardScore from "@ui/components/content-cards/ContentCardScore/ContentCardScore";
import Image from "@ui/components/media/Image";
import MediaIcon from "@ui/components/media/MediaIcon";
import { thumbnailContainerContext } from "@ui/components/media/MediaIcon/MediaIcon.css";
import CommentCallout from "@ui/components/meta/CommentCallout";
import {
  combineComponentContexts,
  useComponentConfig,
} from "@ui/context/component-config-context";
import { useDataAttributes } from "@ui/hooks/use-data-attributes";
import useDefaults from "@ui/hooks/use-defaults";
import { sharedStyles } from "@ui/shared-styles";
import { useGlobalDataContext } from "@voxmedia/duet-context/context/global-data-context";

import * as styles from "./SplitContentCard.css";

export interface SplitContentCardBaseConfig extends ContentCardBaseConfig {
  showBorder: boolean;
  className?: string;
}

interface Props
  extends ContentCardArticle, Partial<SplitContentCardBaseConfig> {
  componentContext?: string;
  gaComponentName: string;
}

const SplitContentCard: FunctionComponent<Props> = (props) => {
  const { componentContext = "splitContentCard" } = props;

  const childComponentContext = combineComponentContexts([
    "splitContentCard",
    componentContext,
  ]);

  const contentCardConfig = useComponentConfig(
    "splitContentCard",
    componentContext || "",
  );

  const { placeholderImageUrl } = useGlobalDataContext().siteSettings;

  const {
    adFlag,
    advertiserName,
    advertiserPreamble,
    authors,
    category,
    chorusId,
    chorusUuid,
    className,
    contentBlockSize,
    contentBlockVariation,
    contentType,
    crossPostSource,
    crossPostUrl,
    commentCountId,
    comments,
    dek,
    gaComponentName,
    globalId,
    headline,
    id,
    live,
    liveBadgeName,
    lowerMetaFirstComponent,
    lowerMetaSecondComponent,
    lowerMetaThirdComponent,
    mediaIconInset,
    mediaVariation,
    mediaVariationIconPlacement,
    metaVariation,
    packageName,
    permalink,
    postTemplate,
    score,
    showCommentCallout,
    showDek,
    showScore,
    site,
    streamName,
    thumbnails,
    timestamp,
    upperMetaFirstComponent,
    upperMetaSecondComponent,
    upperMetaThirdComponent,
  } = useDefaults(props, contentCardConfig);

  return (
    <div
      className={clsx(
        styles.cardLayout,
        sharedStyles.contentCard.coverLinkContentCardContainer,
        thumbnailContainerContext,
        className,
      )}
      {...useDataAttributes(props)}
    >
      <ContentBlockMeta
        adFlag={adFlag}
        advertiserName={advertiserName}
        advertiserPreamble={advertiserPreamble}
        authors={authors}
        category={category}
        chorusId={chorusId}
        chorusUuid={chorusUuid}
        commentCountId={commentCountId}
        componentContext={componentContext}
        contentType={contentType}
        crossPostSource={crossPostSource}
        crossPostUrl={crossPostUrl}
        firstComponent={upperMetaFirstComponent}
        globalId={globalId}
        headline={headline}
        id={id}
        layout="upper"
        live={live}
        liveBadgeName={liveBadgeName}
        metaVariation={metaVariation}
        packageName={packageName}
        permalink={permalink}
        secondComponent={upperMetaSecondComponent}
        site={site}
        streamName={streamName}
        thirdComponent={upperMetaThirdComponent}
        timestamp={timestamp}
      />
      {
        <div className={clsx(styles.imageLayout, thumbnailContainerContext)}>
          <Image
            sizes="221px"
            src={thumbnails?.horizontal || placeholderImageUrl.horizontal}
            alt={headline}
          />
          {mediaVariation && mediaVariationIconPlacement === "thumbnail" && (
            <MediaIcon
              placement="thumbnail"
              variation={mediaVariation}
              inset={mediaIconInset}
            />
          )}
          {score && showScore && <ContentCardScore score={score} />}
          {comments?.enabled && showCommentCallout && commentCountId && (
            <CommentCallout countId={commentCountId} />
          )}
        </div>
      }
      <div className={styles.contentBlockLayout}>
        <ContentBlock
          adFlag={adFlag}
          advertiserName={advertiserName}
          advertiserPreamble={advertiserPreamble}
          authors={authors}
          category={category}
          chorusId={chorusId}
          chorusUuid={chorusUuid}
          commentCountId={commentCountId}
          componentContext={childComponentContext}
          contentBlockSize={contentBlockSize}
          contentBlockVariation={contentBlockVariation}
          contentType={contentType}
          crossPostSource={crossPostSource}
          crossPostUrl={crossPostUrl}
          dek={dek}
          gaComponentName={gaComponentName}
          globalId={globalId}
          headline={headline}
          id={id}
          live={live}
          liveBadgeName={liveBadgeName}
          lowerMetaFirstComponent={lowerMetaFirstComponent}
          lowerMetaSecondComponent={lowerMetaSecondComponent}
          lowerMetaThirdComponent={lowerMetaThirdComponent}
          mediaVariation={mediaVariation}
          mediaVariationIconPlacement={mediaVariationIconPlacement}
          packageName={packageName}
          permalink={permalink}
          postTemplate={postTemplate}
          showDek={showDek}
          site={site}
          streamName={streamName}
          timestamp={timestamp}
        />
      </div>
    </div>
  );
};

export default SplitContentCard;
